import { Combat } from './combat';
import type { Military, Soldier } from './military';
import type { Person } from './population';
import type { WalkNode } from './walking';
import { militaryZone } from '../world/strategic-districts';

interface Notice {
  person: Person;
  state: 'planning' | 'warned' | 'engaging' | 'blocked' | 'cleared';
  exit?: WalkNode;
  route: number[];
  search?: Generator<void, number[]>;
  deadline: number;
  retry: number;
  token: string;
  reason: string;
}
/** Public pavements never trigger this rule. The original civilian must first
 * receive a real route and enough time to walk out of the marked base. */
export class BaseSecurity {
  readonly combat: Combat;
  notices = new Map<string, Notice>();
  onWarning: (text: string) => void = () => {};
  warnings = 0;
  engagements = 0;
  private exits: WalkNode[] = [];
  private age = 0;
  private cursor = 0;
  private sequence = 0;
  private noticeCursor = 0;
  private epoch = 0;
  constructor(readonly army: Military) {
    this.combat = new Combat(army.physics, army.population);
  }
  clear(): void {
    for (const r of this.notices.values()) this.release(r, 'Base security lifecycle cleared');
    this.notices.clear();
    this.combat.reset();
    this.exits = [];
    this.age = this.cursor = this.sequence = this.noticeCursor = this.warnings = this.engagements = 0;
  }
  *resetSteps(): Generator<void> {
    this.clear();
    this.epoch = this.army.physics.life.epoch;
    const gate = this.army.city.strategic?.parcels[0].gate;
    if (!gate) return;
    let count = 0;
    for (const node of this.army.population.civil!.network.nodes) {
      if (++count % 256 === 0) yield;
      if (
        node.layer === 'street' &&
        !node.restricted &&
        !militaryZone(this.army.city, node) &&
        Math.hypot(node.x - gate.x, node.z - gate.z) < 110
      )
        this.exits.push(node);
    }
    this.exits.sort(
      (a, b) => Math.hypot(a.x - gate.x, a.z - gate.z) - Math.hypot(b.x - gate.x, b.z - gate.z),
    );
  }
  private release(r: Notice, reason: string): void {
    const civil = this.army.population.civil;
    if (civil?.task(r.person)?.token === r.token) civil.cancelTask(r.person, reason);
    r.search = undefined;
    r.route = [];
    r.state = 'cleared';
    r.reason = reason;
  }
  private begin(r: Notice): void {
    const { population, time } = this.army,
      network = population.civil!.network;
    const from = network.nearest(r.person.p, r.person.layer);
    const origin = network.nodes[from];
    if (!origin || !network.lineOpen(r.person.p, origin) || population.hazardAt(r.person.p)) {
      r.state = 'blocked';
      r.reason = 'No safe connected exit from the current position; no firing authorisation';
      r.retry = time + 4;
      return;
    }
    const exit = this.exits.find((n) => !population.hazardAt(n) && this.army.physics.ground(n, 0.35, 0.5));
    if (!exit) {
      r.state = 'blocked';
      r.reason = 'Public exit unavailable; no firing authorisation';
      r.retry = time + 4;
      return;
    }
    r.exit = exit;
    r.search = network.search(from, exit.id, time, new Set(), false, true);
    r.state = 'planning';
  }
  private guard(person: Person): Soldier | undefined {
    const candidates: Soldier[] = [];
    const byId = this.army.byId;
    this.army.population.actorsNear(person.p, 140, (actor) => {
      if (
        actor.category !== 'person' ||
        actor.faction !== 'friendly' ||
        actor.fatal ||
        actor.hidden ||
        ['incapacitated', 'falling', 'carried', 'recovering'].includes(actor.state)
      )
        return;
      const soldier = byId.get(actor.id);
      if (soldier && (soldier.state === 'aboard' ? this.army.isGunner(soldier) : !soldier.transition))
        candidates.push(soldier);
    });
    candidates.sort(
      (a, b) => a.person.p.distanceToSquared(person.p) - b.person.p.distanceToSquared(person.p),
    );
    return candidates
      .slice(0, 4)
      .find((s) => this.combat.visible(s.person, person, s.state === 'aboard' ? s.vehicle : undefined));
  }
  private step(r: Notice): void {
    const { population, physics, time } = this.army,
      civil = population.civil!,
      network = civil.network,
      person = r.person;
    if (
      person.fatal ||
      ['incapacitated', 'falling', 'carried', 'recovering'].includes(person.state) ||
      !militaryZone(this.army.city, person.p)
    ) {
      this.release(
        r,
        person.fatal
          ? 'Persistent casualty; security fire stopped'
          : 'Retreat or assistance required; security de-escalated',
      );
      return;
    }
    if (r.state === 'cleared') this.begin(r);
    if (r.state === 'blocked' && time >= r.retry) this.begin(r);
    if (r.search) {
      const next = r.search.next();
      if (!next.done) return;
      r.search = undefined;
      if (!next.value.length) {
        r.state = 'blocked';
        r.retry = time + 5;
        r.reason = 'No reachable public exit; no warning or firing';
        return;
      }
      r.route = next.value;
      let distance = 0;
      for (let i = 1; i < r.route.length; i++) {
        const a = network.nodes[r.route[i - 1]],
          b = network.nodes[r.route[i]];
        distance += Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);
      }
      r.deadline = time + 12 + distance / Math.min(1.5, Math.max(0.6, person.speed));
      r.state = 'warned';
      r.reason = 'Warning issued with a verified public exit and time to walk there';
      r.token = `base-exit:${++this.sequence}:${person.id}`;
      this.warnings++;
      this.onWarning(
        `${person.name}: restricted garrison. Follow the exit route; leave within ${Math.ceil(r.deadline - time)} seconds.`,
      );
      population.audio.play('warning', 0.25, person.p);
      // Ordinary civilians comply through their real controller. An externally
      // interrupted or persistent trespasser still has the documented grace.
      if (civil.prepareForTask(person))
        civil.assignTask(person, civil.nodePlace(r.exit!), r.token, 'leaving restricted area', 0.2);
    }
    if (!['warned', 'engaging'].includes(r.state)) return;
    if (
      r.route.some(
        (node, i) =>
          i > 0 &&
          (!network.edge(r.route[i - 1], node) || !network.open(network.edge(r.route[i - 1], node)!, time)),
      ) ||
      !physics.ground(r.exit!, 0.35, 0.5)
    ) {
      if (civil.task(person)?.token === r.token)
        civil.cancelTask(person, 'Exit changed; security grace suspended');
      r.state = 'blocked';
      r.retry = time + 1;
      r.reason = 'Exit damaged or obstructed; no firing while replanning';
      return;
    }
    if (time < r.deadline || person.carrier || person.hidden || population.hazardAt(person.p)) return;
    const guard = this.guard(person);
    if (!guard || guard.cooldown > 0) {
      r.reason = 'Grace expired; waiting for a physically visible guard';
      return;
    }
    const aim = Math.atan2(guard.person.p.x - person.p.x, guard.person.p.z - person.p.z);
    if (guard.state === 'aboard' && guard.vehicle) guard.vehicle.turretYaw = aim - guard.vehicle.yaw;
    else guard.person.yaw = aim;
    if (!this.combat.active) this.combat.start();
    if (
      this.combat.fire(
        guard.person,
        person,
        this.army.isGunner(guard) ? 'roof-gun' : 'rifle',
        guard.state === 'aboard' ? guard.vehicle : undefined,
      )
    ) {
      guard.cooldown = 1.2;
      r.state = 'engaging';
      r.reason = 'Persistent warned trespass; a real visible guard fired';
      this.engagements++;
    }
  }
  update(dt: number): void {
    if (this.epoch !== this.army.physics.life.epoch) {
      this.combat.cancel();
      return;
    }
    this.combat.update(dt);
    if (this.army.mobilised) {
      if (this.combat.active) this.combat.cancel();
      for (const r of this.notices.values())
        if (r.state !== 'cleared') this.release(r, 'Invasion alert supersedes peacetime trespass procedure');
      return;
    }
    this.age += dt;
    if (this.age < 0.2) return;
    this.age -= 0.2;
    const people = this.army.population.people;
    for (let i = 0; i < Math.min(128, people.length); i++) {
      const person = people[this.cursor++ % people.length];
      if (
        !person.id.startsWith('person-') ||
        person.fatal ||
        person.hidden ||
        person.carrier ||
        !militaryZone(this.army.city, person.p)
      )
        continue;
      if (!this.notices.has(person.id)) {
        const r: Notice = {
          person,
          state: 'cleared',
          route: [],
          deadline: 0,
          retry: 0,
          token: '',
          reason: '',
        };
        this.notices.set(person.id, r);
        this.begin(r);
      }
    }
    const pending = [...this.notices.values()];
    for (let i = 0; i < Math.min(8, pending.length); i++) {
      const r = pending[this.noticeCursor++ % pending.length];
      if (r.state === 'cleared' && !militaryZone(this.army.city, r.person.p)) continue;
      this.step(r);
    }
  }
  render(alpha: number): void {
    this.combat.render(alpha);
  }
  snapshot(): Record<string, number> {
    return {
      baseWarnings: this.warnings,
      baseEngagements: this.engagements,
      baseActiveWarnings: [...this.notices.values()].filter(
        (r) => r.state === 'warned' || r.state === 'engaging',
      ).length,
    };
  }
  inspect(): unknown {
    return {
      ...this.snapshot(),
      notices: [...this.notices.values()].map((r) => ({
        id: r.person.id,
        state: r.state,
        deadline: r.deadline,
        exit: r.exit?.id,
        reason: r.reason,
      })),
      shots: this.combat.log,
    };
  }
  dispose(): void {
    this.clear();
    this.combat.dispose();
  }
}
